import type { IconName } from './icon-names';

/**
 * Semantic icon aliases — intent names that map onto a concrete Feather icon.
 *
 * Nav registries and feature code should prefer these over raw glyph names:
 * `icon: 'learn'` says what the item *is*, where `icon: 'book-open'` says what
 * it looks like. When design swaps the glyph, only this map changes.
 *
 * These also absorb the Lucide → Feather migration. The library's original icon
 * set was Lucide, which is a Feather fork with extra glyphs; the design library
 * standardises on Feather, which has no graduation-cap (`learn`) or
 * layout-dashboard (`dashboard`). The substitutions below were chosen as the
 * closest Feather equivalents and are worth a design review.
 */
export const ICON_ALIASES = {
  dashboard: 'grid',
  content: 'file-text',
  schedule: 'calendar',
  learn: 'book-open',
  resources: 'folder',
  'panel-left': 'sidebar',
  close: 'x',
} as const satisfies Record<string, IconName>;

export type IconAlias = keyof typeof ICON_ALIASES;

/** Any value `<ui-icon name>` accepts: a raw sprite name or a semantic alias. */
export type IconInput = IconName | IconAlias;

/** Resolves an alias to its Feather name; passes raw names through untouched. */
export function resolveIconName(name: IconInput): IconName {
  return (ICON_ALIASES as Record<string, IconName>)[name] ?? (name as IconName);
}
